"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { useApiClient } from "@/api/useApiClient"
import { formatKcal, useI18n } from "@/i18n"

/**
 * ADR-033 (owner incident 2026-08-28): a settings change that alters the
 * calorie target — or reshapes the plan (goal, dietary style, time budget,
 * snacks, constraints) — must INFORM the user and UPDATE the plan for today
 * and the coming week automatically. The previous ADR-023 CTA-only pattern
 * left the plan stale after a wearable-kcal change: the target dropped but
 * every meal stayed sized for the old target.
 *
 * The rebuild is announced, never silent: this banner states the target
 * change (old → new), starts the regeneration job, shows live progress, and
 * reports completion or failure with a retry. Pages call
 * `usePlanSync().start({...})` right after a successful save.
 */

const POLL_INTERVAL_MS = 3000
const POLL_MAX_MS = 4 * 60 * 1000

export type PlanSyncState =
  | { phase: "hidden" }
  | { phase: "updating"; fromKcal: number | null; toKcal: number | null }
  | { phase: "done"; toKcal: number | null }
  | { phase: "error"; fromKcal: number | null; toKcal: number | null }

export function usePlanSync() {
  const api = useApiClient()
  const [state, setState] = useState<PlanSyncState>({ phase: "hidden" })
  const stopRef = useRef<(() => void) | null>(null)

  useEffect(() => () => stopRef.current?.(), [])

  const start = useCallback(
    ({
      fromKcal,
      toKcal,
      jobId,
    }: {
      fromKcal: number | null
      toKcal: number | null
      /** ADR-036: attach to a job the server already queued atomically with the change (step-5/6/7 PUTs return it as triggeredPlanJobId) instead of queueing a duplicate rebuild. */
      jobId?: string | null
    }) => {
      stopRef.current?.()
      setState({ phase: "updating", fromKcal, toKcal })
      let cancelled = false
      const startedAt = Date.now()

      const fail = () => {
        if (!cancelled) setState({ phase: "error", fromKcal, toKcal })
      }

      ;(jobId ? Promise.resolve({ id: jobId }) : api.createPlanJob({ trigger: "regeneration" }))
        .then(job => {
          const timer = window.setInterval(() => {
            if (cancelled) return
            if (Date.now() - startedAt > POLL_MAX_MS) {
              window.clearInterval(timer)
              fail()
              return
            }
            api
              .getPlanJob(job.id)
              .then(status => {
                if (cancelled) return
                if (status.status === "completed") {
                  window.clearInterval(timer)
                  setState({ phase: "done", toKcal })
                } else if (status.status === "failed") {
                  window.clearInterval(timer)
                  fail()
                }
              })
              .catch(() => {
                /* transient poll failure — keep polling until timeout */
              })
          }, POLL_INTERVAL_MS)
          stopRef.current = () => {
            cancelled = true
            window.clearInterval(timer)
          }
        })
        .catch(fail)

      stopRef.current = () => {
        cancelled = true
      }
    },
    [api]
  )

  return { state, start }
}

export function PlanSyncBanner({ state, onRetry }: { state: PlanSyncState; onRetry: () => void }) {
  const { t, locale } = useI18n()
  if (state.phase === "hidden") return null

  const targetChanged =
    state.phase !== "done" &&
    state.fromKcal !== null &&
    state.toKcal !== null &&
    state.fromKcal !== state.toKcal

  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex flex-col gap-2 rounded-bb-md border p-4 ${
        state.phase === "error"
          ? "border-destructive/40 bg-destructive/5"
          : "border-green/40 bg-green/5"
      }`}
    >
      {state.phase === "updating" && (
        <>
          <p className="text-sm font-semibold text-ink">{t("planSync.updatingTitle")}</p>
          {targetChanged && (
            <p className="text-sm text-ink">
              {t("planSync.targetChangedLine", {
                from: formatKcal(state.fromKcal as number, locale),
                to: formatKcal(state.toKcal as number, locale),
              })}
            </p>
          )}
          <p className="text-sm text-ink-2">{t("planSync.updatingBody")}</p>
        </>
      )}
      {state.phase === "done" && (
        <>
          <p className="text-sm font-semibold text-ink">{t("planSync.doneTitle")}</p>
          <p className="text-sm text-ink-2">
            {state.toKcal !== null
              ? t("planSync.doneBodyWithTarget", { to: formatKcal(state.toKcal, locale) })
              : t("planSync.doneBody")}
          </p>
        </>
      )}
      {state.phase === "error" && (
        <>
          <p className="text-sm font-semibold text-ink">{t("planSync.errorTitle")}</p>
          <p className="text-sm text-ink-2">{t("planSync.errorBody")}</p>
          <Button type="button" variant="outline" className="min-h-11 self-start" onClick={onRetry}>
            {t("planSync.retryCta")}
          </Button>
        </>
      )}
    </div>
  )
}
